import { useState } from 'react'
import { 
  Shield, ArrowLeft, Play, X, Check, ArrowRight, 
  FileCheck, Users, TrendingUp, Lock, Crown, Globe,
  Sparkles, Rocket, Star, Building2, Wallet, Scale,
  Briefcase, BarChart3, FileText, Clock, Eye,
  ChevronRight, Download, Upload, Plus, KeyRound,
  User, Mail, Calendar, Award, Linkedin, Twitter,
  ExternalLink, Home, PieChart, FolderOpen,
  ClipboardCheck, UserCheck, Handshake
} from 'lucide-react'

export default function Demo({ onNavigate, onGetStarted }) {
  const [showVideo, setShowVideo] = useState(false)
  const [activeTab, setActiveTab] = useState('overview')
  const [showDocModal, setShowDocModal] = useState(false)
  const [selectedDoc, setSelectedDoc] = useState(null)
  const [showInvestorView, setShowInvestorView] = useState(false)

  // Mock company data - Vobels Limited
  const company = {
    name: 'Vobels Limited',
    industry: 'Technology / SaaS',
    country: 'Nigeria',
    founded: '2023',
    team: 12,
    governanceScore: 92,
    revenue: '₦45.2M',
    description: 'Enterprise SaaS platform for African businesses.',
    documents: {
      uploaded: 18,
      verified: 12,
      pending: 3,
      missing: 2,
    }
  }

  // Mock documents - Generic placeholder names (not real documents)
  const documents = [
    { id: 1, name: 'Certificate of Incorporation', status: 'verified', date: '2024-01-15', type: 'Corporate' },
    { id: 2, name: 'Tax Identification Number', status: 'verified', date: '2024-01-14', type: 'Tax' },
    { id: 3, name: 'Board Resolution', status: 'pending', date: '2024-01-12', type: 'Corporate' },
    { id: 4, name: 'Financial Statement 2024', status: 'uploaded', date: '2024-01-10', type: 'Financial' },
    { id: 5, name: 'Shareholders Agreement', status: 'missing', date: '-', type: 'Legal' },
    { id: 6, name: 'Employee Contracts', status: 'verified', date: '2024-01-08', type: 'HR' },
    { id: 7, name: 'Intellectual Property Assignment', status: 'pending', date: '2024-01-06', type: 'Legal' },
    { id: 8, name: 'VAT Registration', status: 'verified', date: '2024-01-04', type: 'Tax' },
  ]

  // Mock staff members
  const staff = [
    { id: 1, name: 'Chidi Okafor', role: 'Founder & CEO', department: 'Executive', email: 'chidi@vobels.com' },
    { id: 2, name: 'Ngozi Okonkwo', role: 'CFO', department: 'Finance', email: 'ngozi@vobels.com' },
    { id: 3, name: 'Tunde Bakare', role: 'Head of Legal', department: 'Legal', email: 'tunde@vobels.com' },
    { id: 4, name: 'Amara Obi', role: 'Compliance Officer', department: 'Compliance', email: 'amara@vobels.com' },
  ]

  // Mock agent (Acme)
  const agent = {
    name: 'Acme Compliance Partners',
    type: 'Legal & Compliance Agent',
    country: 'Nigeria',
    rating: 4.9,
    reviews: 127,
    documentsHandled: 48,
    specialties: ['Corporate Law', 'Tax Compliance', 'SEC Filings'],
  }

  // Mock investor stats
  const investorStats = {
    tokensGenerated: 12,
    activeTokens: 8,
    views: 47,
    lastAccess: '2024-01-15',
  }

  // Mock investor tokens for investor view
  const investorTokens = [
    { 
      id: 1, 
      investor: 'David Okonkwo', 
      fund: 'Halan Ventures', 
      token: 'INV-TOK-0091', 
      expires: '2024-03-15',
      status: 'active',
      viewed: true,
      lastViewed: '2024-01-14'
    },
    { 
      id: 2, 
      investor: 'Sarah Mitchell', 
      fund: 'Orbit Capital', 
      token: 'INV-TOK-0092', 
      expires: '2024-04-01',
      status: 'active',
      viewed: false,
      lastViewed: '-'
    },
    { 
      id: 3, 
      investor: 'Michael Brennan', 
      fund: 'Sequoia Africa', 
      token: 'INV-TOK-0093', 
      expires: '2024-02-28',
      status: 'active',
      viewed: true,
      lastViewed: '2024-01-12'
    },
  ]

  // Mock investor view of company documents
  const investorVisibleDocs = documents.filter(d => d.status === 'verified' || d.status === 'uploaded')

  const getStatusColor = (status) => {
    const colors = {
      verified: 'bg-emerald-100 text-emerald-700 border-emerald-300',
      uploaded: 'bg-blue-100 text-blue-700 border-blue-300',
      pending: 'bg-amber-100 text-amber-700 border-amber-300',
      missing: 'bg-rose-100 text-rose-700 border-rose-300',
      active: 'bg-emerald-100 text-emerald-700 border-emerald-300',
      expired: 'bg-rose-100 text-rose-700 border-rose-300',
    }
    return colors[status] || 'bg-neutral-100 text-neutral-700 border-neutral-300'
  }

  const getStatusIcon = (status) => {
    const icons = {
      verified: <Check size={12} className="text-emerald-600" />,
      uploaded: <Upload size={12} className="text-blue-600" />,
      pending: <Clock size={12} className="text-amber-600" />,
      missing: <X size={12} className="text-rose-600" />,
      active: <Check size={12} className="text-emerald-600" />,
      expired: <X size={12} className="text-rose-600" />,
    }
    return icons[status] || null
  }

  const toggleInvestorView = () => {
    setShowInvestorView(!showInvestorView)
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-sm border-b border-neutral-100">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-ink rounded-lg flex items-center justify-center">
              <Shield size={18} className="text-white" />
            </div>
            <span className="font-bold text-lg text-ink">Bizos Suite</span>
            <span className="text-xs text-brand bg-brand-soft px-2 py-0.5 rounded-full font-semibold">
              Demo
            </span>
          </div>
          <div className="flex items-center gap-4">
            {/* Investor View Toggle */}
            <button 
              onClick={toggleInvestorView}
              className={`text-sm font-medium px-3 py-1.5 rounded-lg transition-colors ${
                showInvestorView 
                  ? 'bg-brand text-white' 
                  : 'text-neutral-600 hover:bg-neutral-100'
              }`}
            >
              {showInvestorView ? 'Founder View' : 'Investor View'}
            </button>
            <button onClick={() => onNavigate('landing')} className="text-sm font-medium text-neutral-600 hover:text-brand transition-colors">
              Home
            </button>
            <button onClick={onGetStarted} className="text-sm font-semibold text-brand hover:text-brand-dark transition-colors">
              Sign In
            </button>
            <button onClick={() => onNavigate('pricing')} className="btn-primary text-sm">
              Get Started
            </button>
          </div>
        </div>
      </nav>

      {/* ============================================================
          FOUNDER VIEW
          ============================================================ */}
      {!showInvestorView ? (

        /* FOUNDER DASHBOARD */
        <div>
          {/* Hero - Vobels Limited */}
          <section className="pt-24 pb-6 px-6 bg-gradient-to-br from-brand-soft/30 to-white border-b border-neutral-100">
            <div className="max-w-7xl mx-auto">
              <button onClick={() => onNavigate('landing')} className="inline-flex items-center gap-1.5 text-sm text-muted hover:text-ink transition-colors mb-4">
                <ArrowLeft size={16} /> Back to Home
              </button>
              
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-3">
                    <div className="w-14 h-14 bg-brand rounded-xl flex items-center justify-center shadow-lg">
                      <Building2 size={28} className="text-white" />
                    </div>
                    <div>
                      <h1 className="text-2xl md:text-3xl font-bold text-ink">{company.name}</h1>
                      <div className="flex items-center gap-3 mt-1 text-sm text-muted">
                        <span>{company.industry}</span>
                        <span>•</span>
                        <span>{company.country}</span>
                        <span>•</span>
                        <span>Founded {company.founded}</span>
                        <span>•</span>
                        <span>{company.team} employees</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="text-xs text-muted uppercase tracking-wider">Governance Score</p>
                    <p className="text-3xl font-bold text-brand">{company.governanceScore}%</p>
                  </div>
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-emerald-100 text-emerald-700 text-xs font-semibold border border-emerald-200 rounded-full">
                    <Rocket size={12} /> Venture-Grade
                  </span>
                </div>
              </div>

              {/* Demo Banner */}
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-3">
                <Sparkles size={16} className="text-amber-600 shrink-0" />
                <p className="text-xs text-amber-800">
                  <span className="font-semibold">Demo Mode:</span> This is a sample dashboard showing how Vobels Limited uses Bizos Suite. 
                  All data is for demonstration purposes only.
                </p>
              </div>
            </div>
          </section>

          {/* Dashboard Tabs */}
          <section className="px-6 py-6 bg-white border-b border-neutral-100">
            <div className="max-w-7xl mx-auto">
              <div className="flex flex-wrap gap-2">
                {['overview', 'documents', 'staff', 'investor'].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`px-4 py-2 text-sm font-semibold rounded-lg transition-colors flex items-center gap-2 ${
                      activeTab === tab 
                        ? 'bg-brand text-white' 
                        : 'text-neutral-600 hover:bg-neutral-100'
                    }`}
                  >
                    {tab === 'overview' && <Home size={16} />}
                    {tab === 'documents' && <FileText size={16} />}
                    {tab === 'staff' && <Users size={16} />}
                    {tab === 'investor' && <Crown size={16} />}
                    {tab === 'overview' && 'Overview'}
                    {tab === 'documents' && 'Documents'}
                    {tab === 'staff' && 'Staff'}
                    {tab === 'investor' && 'Investor Portal'}
                  </button>
                ))}
                <button 
                  onClick={toggleInvestorView}
                  className="ml-auto text-xs text-neutral-400 hover:text-brand transition-colors px-3 py-2 flex items-center gap-1.5"
                >
                  <Eye size={14} /> Switch to Investor View
                </button>
              </div>
            </div>
          </section>

          {/* Dashboard Content */}
          <section className="px-6 py-8 bg-neutral-50 min-h-[500px]">
            <div className="max-w-7xl mx-auto">
              
              {/* ============================================================
                  OVERVIEW TAB
                  ============================================================ */}
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  {/* KPI Cards */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-white border border-neutral-200 rounded-xl p-5">
                      <p className="text-xs text-muted uppercase tracking-wider">Revenue</p>
                      <p className="text-2xl font-bold text-ink mt-1">{company.revenue}</p>
                      <span className="inline-flex items-center gap-1 text-xs text-emerald-600">
                        <TrendingUp size={12} /> +12% vs last year
                      </span>
                    </div>
                    <div className="bg-white border border-neutral-200 rounded-xl p-5">
                      <p className="text-xs text-muted uppercase tracking-wider">Documents</p>
                      <p className="text-2xl font-bold text-ink mt-1">{company.documents.uploaded}</p>
                      <span className="inline-flex items-center gap-1 text-xs text-muted">
                        <Check size={12} className="text-emerald-600" /> {company.documents.verified} verified
                      </span>
                    </div>
                    <div className="bg-white border border-neutral-200 rounded-xl p-5">
                      <p className="text-xs text-muted uppercase tracking-wider">Team Members</p>
                      <p className="text-2xl font-bold text-ink mt-1">{company.team}</p>
                      <span className="inline-flex items-center gap-1 text-xs text-muted">
                        <Users size={12} /> Across 4 departments
                      </span>
                    </div>
                    <div className="bg-white border border-neutral-200 rounded-xl p-5">
                      <p className="text-xs text-muted uppercase tracking-wider">Governance Score</p>
                      <p className="text-2xl font-bold text-brand mt-1">{company.governanceScore}%</p>
                      <span className="inline-flex items-center gap-1 text-xs text-emerald-600">
                        <Rocket size={12} /> Venture-Grade
                      </span>
                    </div>
                  </div>

                  {/* Document Progress */}
                  <div className="bg-white border border-neutral-200 rounded-xl p-6">
                    <h3 className="text-sm font-semibold text-ink mb-4">Document Progress</h3>
                    <div className="grid grid-cols-4 gap-4">
                      {Object.entries(company.documents).map(([key, value]) => (
                        <div key={key} className="text-center">
                          <p className={`text-2xl font-bold ${
                            key === 'verified' ? 'text-emerald-600' :
                            key === 'pending' ? 'text-amber-600' :
                            key === 'missing' ? 'text-rose-600' : 'text-brand'
                          }`}>{value}</p>
                          <p className="text-xs text-muted capitalize">{key}</p>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 w-full h-2 bg-neutral-100 rounded-full overflow-hidden">
                      <div className="h-full bg-brand rounded-full" style={{ width: '78%' }} />
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="bg-white border border-neutral-200 rounded-xl p-6">
                    <h3 className="text-sm font-semibold text-ink mb-4">Quick Actions</h3>
                    <div className="flex flex-wrap gap-3">
                      <button className="btn-primary text-xs flex items-center gap-1.5">
                        <Upload size={14} /> Upload Document
                      </button>
                      <button className="btn-secondary text-xs flex items-center gap-1.5">
                        <Plus size={14} /> Add Staff
                      </button>
                      <button className="btn-secondary text-xs flex items-center gap-1.5">
                        <KeyRound size={14} /> Generate Token
                      </button>
                      <button className="btn-secondary text-xs flex items-center gap-1.5">
                        <FileText size={14} /> View Reports
                      </button>
                    </div>
                  </div>

                  {/* Demo Note */}
                  <div className="bg-brand-soft border border-brand/20 rounded-xl p-4 text-center">
                    <p className="text-sm text-brand font-medium">This is a demo dashboard for Vobels Limited</p>
                    <p className="text-xs text-muted mt-1">Your dashboard will look similar with your actual company data</p>
                  </div>
                </div>
              )}

              {/* ============================================================
                  DOCUMENTS TAB
                  ============================================================ */}
              {activeTab === 'documents' && (
                <div className="space-y-6">
                  <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
                    <div className="grid grid-cols-12 px-6 py-3 bg-neutral-50 border-b border-neutral-200 text-xs font-semibold uppercase tracking-wider text-muted">
                      <div className="col-span-5">Document Name</div>
                      <div className="col-span-3">Type</div>
                      <div className="col-span-2">Date</div>
                      <div className="col-span-2 text-right">Status</div>
                    </div>
                    {documents.map((doc) => (
                      <div 
                        key={doc.id} 
                        className="grid grid-cols-12 px-6 py-3 border-b border-neutral-100 items-center hover:bg-neutral-50 transition-colors cursor-pointer"
                        onClick={() => { setSelectedDoc(doc); setShowDocModal(true) }}
                      >
                        <div className="col-span-5 flex items-center gap-2">
                          <FileText size={14} className="text-neutral-400" />
                          <span className="text-sm text-ink font-medium">{doc.name}</span>
                        </div>
                        <div className="col-span-3 text-sm text-muted">{doc.type}</div>
                        <div className="col-span-2 text-sm text-muted">{doc.date}</div>
                        <div className="col-span-2 flex justify-end">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold border rounded-full ${getStatusColor(doc.status)}`}>
                            {getStatusIcon(doc.status)} {doc.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Document Stats */}
                  <div className="bg-white border border-neutral-200 rounded-xl p-6">
                    <h4 className="text-sm font-semibold text-ink mb-3">Document Summary</h4>
                    <div className="grid grid-cols-4 gap-4">
                      <div>
                        <p className="text-xs text-muted">Total</p>
                        <p className="text-lg font-bold text-ink">{documents.length}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted">Verified</p>
                        <p className="text-lg font-bold text-emerald-600">
                          {documents.filter(d => d.status === 'verified').length}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted">Pending</p>
                        <p className="text-lg font-bold text-amber-600">
                          {documents.filter(d => d.status === 'pending').length}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted">Missing</p>
                        <p className="text-lg font-bold text-rose-600">
                          {documents.filter(d => d.status === 'missing').length}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* ============================================================
                  STAFF TAB
                  ============================================================ */}
              {activeTab === 'staff' && (
                <div className="space-y-6">
                  <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
                    <div className="grid grid-cols-12 px-6 py-3 bg-neutral-50 border-b border-neutral-200 text-xs font-semibold uppercase tracking-wider text-muted">
                      <div className="col-span-4">Name</div>
                      <div className="col-span-3">Role</div>
                      <div className="col-span-3">Department</div>
                      <div className="col-span-2 text-right">Status</div>
                    </div>
                    {staff.map((member) => (
                      <div key={member.id} className="grid grid-cols-12 px-6 py-3 border-b border-neutral-100 items-center">
                        <div className="col-span-4 flex items-center gap-3">
                          <div className="w-8 h-8 bg-brand-soft rounded-full flex items-center justify-center text-brand font-semibold text-xs">
                            {member.name.split(' ').map(n => n[0]).join('')}
                          </div>
                          <span className="text-sm text-ink font-medium">{member.name}</span>
                        </div>
                        <div className="col-span-3 text-sm text-muted">{member.role}</div>
                        <div className="col-span-3 text-sm text-muted">{member.department}</div>
                        <div className="col-span-2 flex justify-end">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold border border-emerald-300 text-emerald-700 bg-emerald-50 rounded-full">
                            <UserCheck size={10} /> Active
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Agent Card */}
                  <div className="bg-white border border-neutral-200 rounded-xl p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                          <Briefcase size={20} className="text-amber-600" />
                        </div>
                        <div>
                          <h4 className="text-sm font-semibold text-ink">{agent.name}</h4>
                          <p className="text-xs text-muted">{agent.type} • {agent.country}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="inline-flex items-center gap-1 text-xs text-amber-600 font-semibold">
                              <Star size={12} className="fill-amber-500" /> {agent.rating}
                            </span>
                            <span className="text-xs text-muted">({agent.reviews} reviews)</span>
                            <span className="text-xs text-muted">•</span>
                            <span className="text-xs text-muted">{agent.documentsHandled} documents handled</span>
                          </div>
                        </div>
                      </div>
                      <button className="btn-primary text-xs flex items-center gap-1.5">
                        <Handshake size={14} /> View Agent
                      </button>
                    </div>
                    {/* Agent Specialties */}
                    <div className="mt-4 pt-4 border-t border-neutral-100 flex flex-wrap gap-2">
                      {agent.specialties.map((spec) => (
                        <span key={spec} className="px-2 py-1 text-xs bg-neutral-100 text-neutral-600 rounded-lg border border-neutral-200">
                          {spec}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* ============================================================
                  INVESTOR PORTAL TAB
                  ============================================================ */}
              {activeTab === 'investor' && (
                <div className="space-y-6">
                  {/* Investor Stats */}
                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                    <div className="bg-white border border-neutral-200 rounded-xl p-5">
                      <p className="text-xs text-muted uppercase tracking-wider">Tokens Generated</p>
                      <p className="text-2xl font-bold text-ink mt-1">{investorStats.tokensGenerated}</p>
                    </div>
                    <div className="bg-white border border-neutral-200 rounded-xl p-5">
                      <p className="text-xs text-muted uppercase tracking-wider">Active Tokens</p>
                      <p className="text-2xl font-bold text-emerald-600 mt-1">{investorStats.activeTokens}</p>
                    </div>
                    <div className="bg-white border border-neutral-200 rounded-xl p-5">
                      <p className="text-xs text-muted uppercase tracking-wider">Total Views</p>
                      <p className="text-2xl font-bold text-brand mt-1">{investorStats.views}</p>
                    </div>
                    <div className="bg-white border border-neutral-200 rounded-xl p-5">
                      <p className="text-xs text-muted uppercase tracking-wider">Last Access</p>
                      <p className="text-lg font-bold text-ink mt-1">{investorStats.lastAccess}</p>
                    </div>
                  </div>

                  {/* Active Tokens */}
                  <div className="bg-white border border-neutral-200 rounded-xl p-6">
                    <h3 className="text-sm font-semibold text-ink mb-4 flex items-center gap-2">
                      <KeyRound size={16} className="text-brand" />
                      Active Investor Tokens
                    </h3>
                    <div className="divide-y divide-neutral-100">
                      {investorTokens.map((inv) => (
                        <div key={inv.id} className="py-3 flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-ink">{inv.investor}</p>
                            <p className="text-xs text-muted">{inv.fund}</p>
                            <p className="text-xs font-mono text-muted mt-0.5">{inv.token}</p>
                          </div>
                          <div className="text-right">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold border rounded-full ${getStatusColor(inv.status)}`}>
                              {inv.status === 'active' ? <Check size={10} /> : <X size={10} />}
                              {inv.status}
                            </span>
                            <p className="text-xs text-muted mt-1">Expires: {inv.expires}</p>
                            {inv.viewed && (
                              <p className="text-xs text-emerald-600 mt-0.5">Viewed: {inv.lastViewed}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    <button className="mt-4 btn-primary text-xs flex items-center gap-1.5">
                      <Plus size={14} /> Generate New Token
                    </button>
                  </div>

                  {/* Investor Documents (What they see) */}
                  <div className="bg-white border border-neutral-200 rounded-xl p-6">
                    <h3 className="text-sm font-semibold text-ink mb-4 flex items-center gap-2">
                      <Eye size={16} className="text-brand" />
                      Documents Visible to Investors
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {investorVisibleDocs.map((doc) => (
                        <div key={doc.id} className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg border border-neutral-200">
                          <div className="flex items-center gap-2">
                            <FileText size={14} className="text-neutral-400" />
                            <span className="text-sm text-ink">{doc.name}</span>
                          </div>
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold border rounded-full ${getStatusColor(doc.status)}`}>
                            {getStatusIcon(doc.status)} {doc.status}
                          </span>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-muted mt-3">Investors see verified and uploaded documents only</p>
                  </div>
                </div>
              )}
            </div>
          </section>
        </div>
      ) : (

        /* ============================================================
           INVESTOR VIEW - What investors see when they access the vault
           ============================================================ */
        <div>
          {/* Investor View Header */}
          <section className="pt-24 pb-6 px-6 bg-gradient-to-br from-emerald-50/30 to-white border-b border-neutral-100">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center gap-2 mb-4">
                <button onClick={toggleInvestorView} className="inline-flex items-center gap-1.5 text-sm text-muted hover:text-ink transition-colors">
                  <ArrowLeft size={16} /> Back to Founder View
                </button>
                <span className="text-xs text-emerald-600 bg-emerald-100 px-2 py-0.5 rounded-full font-semibold ml-2">
                  Investor View
                </span>
              </div>
              
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-3">
                    <div className="w-14 h-14 bg-brand rounded-xl flex items-center justify-center shadow-lg">
                      <Building2 size={28} className="text-white" />
                    </div>
                    <div>
                      <h1 className="text-2xl md:text-3xl font-bold text-ink">{company.name}</h1>
                      <div className="flex items-center gap-3 mt-1 text-sm text-muted">
                        <span>{company.industry}</span>
                        <span>•</span>
                        <span>{company.country}</span>
                      </div>
                      <p className="text-xs text-muted mt-1 max-w-md">{company.description}</p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="text-xs text-muted uppercase tracking-wider">Governance Score</p>
                    <p className="text-3xl font-bold text-brand">{company.governanceScore}%</p>
                  </div>
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-emerald-100 text-emerald-700 text-xs font-semibold border border-emerald-200 rounded-full">
                    <Shield size={12} /> Verified
                  </span>
                </div>
              </div>

              {/* Investor Demo Banner */}
              <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center gap-3">
                <Eye size={16} className="text-emerald-600 shrink-0" />
                <p className="text-xs text-emerald-800">
                  <span className="font-semibold">Investor View:</span> This is how investors see your company vault. 
                  Only verified and uploaded documents are visible.
                </p>
              </div>
            </div>
          </section>

          {/* Investor Dashboard Content */}
          <section className="px-6 py-8 bg-neutral-50 min-h-[500px]">
            <div className="max-w-7xl mx-auto">
              {/* Investor KPI Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                <div className="bg-white border border-neutral-200 rounded-xl p-5">
                  <p className="text-xs text-muted uppercase tracking-wider">Verified Documents</p>
                  <p className="text-2xl font-bold text-emerald-600 mt-1">
                    {documents.filter(d => d.status === 'verified').length}
                  </p>
                </div>
                <div className="bg-white border border-neutral-200 rounded-xl p-5">
                  <p className="text-xs text-muted uppercase tracking-wider">Governance Score</p>
                  <p className="text-2xl font-bold text-brand mt-1">{company.governanceScore}%</p>
                </div>
                <div className="bg-white border border-neutral-200 rounded-xl p-5">
                  <p className="text-xs text-muted uppercase tracking-wider">Last Updated</p>
                  <p className="text-lg font-bold text-ink mt-1">2024-01-15</p>
                </div>
              </div>

              {/* Documents Available to Investor */}
              <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
                <div className="px-6 py-4 bg-neutral-50 border-b border-neutral-200 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-ink flex items-center gap-2">
                    <FolderOpen size={16} className="text-brand" />
                    Available Documents
                  </h3>
                  <span className="text-xs text-muted">{investorVisibleDocs.length} documents</span>
                </div>
                <div className="divide-y divide-neutral-100">
                  {investorVisibleDocs.map((doc) => (
                    <div key={doc.id} className="px-6 py-3 flex items-center justify-between hover:bg-neutral-50 transition-colors">
                      <div className="flex items-center gap-3">
                        <FileText size={16} className="text-neutral-400" />
                        <div>
                          <p className="text-sm font-medium text-ink">{doc.name}</p>
                          <p className="text-xs text-muted">{doc.type}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold border rounded-full ${getStatusColor(doc.status)}`}>
                          {getStatusIcon(doc.status)} {doc.status}
                        </span>
                        <button className="text-brand hover:text-brand-dark transition-colors p-1">
                          <Eye size={16} />
                        </button>
                        <button className="text-neutral-400 hover:text-brand transition-colors p-1">
                          <Download size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Investor View Note */}
              <div className="mt-6 bg-white border border-neutral-200 rounded-xl p-4 text-center">
                <p className="text-xs text-muted">
                  <Shield size={14} className="inline mr-1 text-emerald-600" /> 
                  This is a read-only view. Investors can view and download verified documents only.
                </p>
                <p className="text-xs text-muted mt-1">
                  Token: <span className="font-mono text-brand">INV-TOK-0091</span> • Expires: 2024-03-15
                </p>
              </div>
            </div>
          </section>
        </div>
      )}

      {/* Document Detail Modal */}
      {showDocModal && selectedDoc && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-ink/60">
          <div className="relative bg-white rounded-2xl max-w-md w-full p-6 animate-scale-in">
            <button 
              onClick={() => setShowDocModal(false)} 
              className="absolute top-4 right-4 text-neutral-400 hover:text-ink transition-colors"
            >
              <X size={20} />
            </button>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-brand-soft rounded-lg flex items-center justify-center">
                <FileText size={18} className="text-brand" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-ink">{selectedDoc.name}</h3>
                <p className="text-xs text-muted">{selectedDoc.type}</p>
              </div>
            </div>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted">Status</span>
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold border rounded-full ${getStatusColor(selectedDoc.status)}`}>
                  {getStatusIcon(selectedDoc.status)} {selectedDoc.status}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted">Date Uploaded</span>
                <span className="text-ink">{selectedDoc.date}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted">Category</span>
                <span className="text-ink">{selectedDoc.type}</span>
              </div>
            </div>
            <div className="mt-6 flex gap-3">
              <button className="btn-primary text-sm flex-1 flex items-center justify-center gap-1.5">
                <Eye size={14} /> View Document
              </button>
              <button className="btn-secondary text-sm flex-1 flex items-center justify-center gap-1.5">
                <Download size={14} /> Download
              </button>
            </div>
            <p className="mt-4 text-[10px] text-muted text-center">Demo mode - Document content is placeholder</p>
          </div>
        </div>
      )}

      {/* Video Modal */}
      {showVideo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-ink/80">
          <div className="relative bg-white rounded-2xl max-w-3xl w-full p-4">
            <button 
              onClick={() => setShowVideo(false)} 
              className="absolute -top-10 right-0 text-white hover:text-neutral-300 transition-colors"
            >
              <X size={24} />
            </button>
            <div className="aspect-video bg-ink rounded-lg flex items-center justify-center">
              <div className="text-center text-white/60">
                <p className="text-lg">Demo video coming soon</p>
                <p className="text-sm mt-2">Explore the demo dashboard above</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-neutral-200 bg-white">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-muted">
          <span>© 2026 Bizos Suite. All rights reserved. Built in Nigeria for Africa.</span>
          <div className="flex items-center gap-4">
            <span className="text-neutral-300">|</span>
            <span className="text-brand">Demo Mode</span>
            <span className="text-neutral-300">|</span>
            <span>Sample data for Vobels Limited</span>
            {showInvestorView && (
              <>
                <span className="text-neutral-300">|</span>
                <span className="text-emerald-600">Investor View</span>
              </>
            )}
          </div>
        </div>
      </footer>
    </div>
  )
}